import React, { useEffect, useRef, useState } from 'react';
import axios from '../../library/axios';
import { MySwal } from '../../library/swal';
import CpanelPageShell from '../../components/cpanel/CpanelPageShell';
import CentralUserAvatar from '../../components/cpanel/CentralUserAvatar';
import {
  formatCentralRoleLabel,
  getCentralUser,
  notifyCentralUserUpdated,
} from '../../helpers/cpanelAuth';
import { getApiErrorMessage } from '../../helpers/apiErrorMessage';

const ACCEPTED_AVATAR_TYPES = 'image/jpeg,image/png,image/webp,image/gif';

const CpanelProfilePage = () => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [user, setUser] = useState(() => getCentralUser());
  const [username, setUsername] = useState('');
  const [namaLengkap, setNamaLengkap] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const applyUser = (nextUser) => {
    if (!nextUser) return;
    setUser(nextUser);
    setUsername(nextUser.username || '');
    setNamaLengkap(nextUser.namaLengkap || '');
    setEmail(nextUser.email || '');
    setRole(nextUser.role || '');
    notifyCentralUserUpdated(nextUser);
  };

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      const cached = getCentralUser();
      if (cached) {
        setUser(cached);
        setUsername(cached.username || '');
        setNamaLengkap(cached.namaLengkap || '');
        setEmail(cached.email || '');
        setRole(cached.role || '');
      }

      try {
        const { data } = await axios.get('/cpanel/auth/me');
        if (cancelled || !data?.user) return;
        applyUser({ ...cached, ...data.user });
      } catch {
        if (!cancelled && !cached) {
          MySwal.fire({
            icon: 'error',
            title: 'Gagal memuat profil',
            text: 'Tidak dapat mengambil data profil.',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAvatarSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!ACCEPTED_AVATAR_TYPES.split(',').includes(file.type)) {
      MySwal.fire({
        icon: 'warning',
        title: 'Format tidak didukung',
        text: 'Gunakan JPG, PNG, WEBP, atau GIF (maks. 2 MB).',
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      MySwal.fire({
        icon: 'warning',
        title: 'File terlalu besar',
        text: 'Ukuran avatar maksimal 2 MB.',
      });
      return;
    }

    const formData = new FormData();
    formData.append('avatar', file);

    setUploadingAvatar(true);
    try {
      const { data } = await axios.post('/cpanel/auth/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data?.user) {
        applyUser({ ...getCentralUser(), ...data.user });
      }
      MySwal.fire({
        icon: 'success',
        title: 'Avatar diperbarui',
        text: data?.message || 'Foto profil berhasil diunggah.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      MySwal.fire({
        icon: 'error',
        title: 'Gagal mengunggah avatar',
        text: getApiErrorMessage(err, 'Tidak dapat menyimpan avatar.'),
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleResetAvatar = async () => {
    const confirm = await MySwal.fire({
      icon: 'question',
      title: 'Kembalikan avatar default?',
      text: 'Foto profil kustom akan dihapus.',
      showCancelButton: true,
      confirmButtonText: 'Ya, kembalikan',
      cancelButtonText: 'Batal',
    });
    if (!confirm.isConfirmed) return;

    setUploadingAvatar(true);
    try {
      const { data } = await axios.post('/cpanel/auth/profile/avatar/reset');
      if (data?.user) {
        applyUser({ ...getCentralUser(), ...data.user });
      }
      MySwal.fire({
        icon: 'success',
        title: 'Avatar default',
        text: data?.message || 'Avatar dikembalikan ke default.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      MySwal.fire({
        icon: 'error',
        title: 'Gagal mengubah avatar',
        text: getApiErrorMessage(err, 'Tidak dapat mengembalikan avatar.'),
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password && password.length < 6) {
      MySwal.fire({
        icon: 'warning',
        title: 'Password terlalu pendek',
        text: 'Password baru minimal 6 karakter.',
      });
      return;
    }

    if (password && password !== confirmPassword) {
      MySwal.fire({
        icon: 'warning',
        title: 'Konfirmasi tidak cocok',
        text: 'Konfirmasi password baru harus sama.',
      });
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.patch('/cpanel/auth/profile', {
        username: username.trim(),
        namaLengkap: namaLengkap.trim(),
        email: email.trim(),
        password,
        confirmPassword,
      });

      if (data?.user) {
        applyUser({ ...getCentralUser(), ...data.user });
      }

      setPassword('');
      setConfirmPassword('');

      MySwal.fire({
        icon: 'success',
        title: 'Profil diperbarui',
        text: data?.message || 'Perubahan profil berhasil disimpan.',
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      MySwal.fire({
        icon: 'error',
        title: 'Gagal menyimpan',
        text: getApiErrorMessage(err, 'Tidak dapat memperbarui profil.'),
      });
    } finally {
      setSaving(false);
    }
  };

  const displayName = user?.namaLengkap || user?.username || 'Administrator';

  return (
    <CpanelPageShell
      title="Profil"
      subtitle="Akun saya"
      breadcrumbs={[
        { label: 'CPanel Center', to: '/cpanel/dashboard' },
        { label: 'Profil' },
      ]}
    >
      <div className="card cpanel-card">
        <div className="card-header border-0 pt-6">
          <h3 className="card-title fw-bold text-gray-800 mb-0">Edit profil</h3>
        </div>
        <div className="card-body pt-2">
          {loading ? (
            <p className="text-muted mb-0">Memuat profil...</p>
          ) : (
            <>
              <div className="cpanel-profile-avatar mb-8">
                <CentralUserAvatar user={user} displayName={displayName} size="xl" />
                <div className="cpanel-profile-avatar__actions">
                  <p className="fw-semibold text-gray-800 mb-1">Foto profil</p>
                  <p className="text-muted fs-7 mb-4">
                    JPG, PNG, WEBP, atau GIF. Maksimal 2 MB.
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-light-primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAvatar}
                    >
                      {uploadingAvatar ? 'Mengunggah...' : 'Ubah avatar'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={handleResetAvatar}
                      disabled={uploadingAvatar}
                    >
                      Avatar default
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_AVATAR_TYPES}
                    className="d-none"
                    onChange={handleAvatarSelect}
                  />
                </div>
              </div>

              <div className="separator separator-dashed mb-8" />

              <form className="mw-600px" onSubmit={handleSubmit}>
                <div className="mb-5">
                  <label className="form-label fw-semibold text-gray-700" htmlFor="profile-role">
                    Role
                  </label>
                  <input
                    id="profile-role"
                    className="form-control form-control-solid"
                    type="text"
                    value={formatCentralRoleLabel(role) || role}
                    readOnly
                    disabled
                  />
                  <div className="form-text">Role hanya dapat diubah oleh Super Admin.</div>
                </div>

                <div className="mb-5">
                  <label className="form-label fw-semibold text-gray-700 required" htmlFor="profile-username">
                    Username
                  </label>
                  <input
                    id="profile-username"
                    className="form-control"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </div>

                <div className="mb-5">
                  <label className="form-label fw-semibold text-gray-700 required" htmlFor="profile-name">
                    Nama lengkap
                  </label>
                  <input
                    id="profile-name"
                    className="form-control"
                    type="text"
                    value={namaLengkap}
                    onChange={(e) => setNamaLengkap(e.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="mb-5">
                  <label className="form-label fw-semibold text-gray-700" htmlFor="profile-email">
                    Email
                  </label>
                  <input
                    id="profile-email"
                    className="form-control"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>

                <div className="separator separator-dashed my-8" />

                <p className="text-muted fs-7 mb-5">
                  Kosongkan field password baru jika tidak ingin mengubah password.
                </p>

                <div className="mb-5">
                  <label className="form-label fw-semibold text-gray-700" htmlFor="profile-password">
                    Password baru
                  </label>
                  <input
                    id="profile-password"
                    className="form-control"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={6}
                  />
                </div>

                <div className="mb-8">
                  <label className="form-label fw-semibold text-gray-700" htmlFor="profile-confirm-password">
                    Konfirmasi password baru
                  </label>
                  <input
                    id="profile-confirm-password"
                    className="form-control"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={6}
                  />
                </div>

                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Menyimpan...' : 'Simpan perubahan'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </CpanelPageShell>
  );
};

export default CpanelProfilePage;
